<template>
  <div v-if="productDetail && !errorState">
    <AtomicUiNotification v-if="notification.show" severity="error" :closable="false" class="margin-top-xs">
      {{ notification.message }}
    </AtomicUiNotification>
    <div v-if="!isRegistrant">
      <AtomicUiButton
        v-if="isAvailable"
        variant="primary"
        :expanded="true"
        class="margin-top-m"
        :disabled="enableQuantityDropdown"
        :loading="isAddToBagLoading"
        @click="handleAddToBag"
      >
        {{ quickViewSlideOut.myGiftsDetailsATB }}
      </AtomicUiButton>
      <AtomicUiButton v-if="!isAvailable" variant="primary" :expanded="true" class="margin-top-m" :disabled="true"> Out Of Stock </AtomicUiButton>
      <div v-if="showGiftGiverViewProductDetailsLink" class="text-center margin-top-xs">
        <a class="secondary-cta" target="blank" :href="productDetail?.identifier?.productUrl">View full product details</a>
      </div>
    </div>
    <div v-if="isRegistrant">
      <AtomicUiButton
        variant="primary"
        :expanded="true"
        class="margin-top-m"
        :disabled="disableSaveBtn || saveChangesButtonDisabled"
        @click="saveRegistrantQuantityChanges"
      >
        {{ quickViewSlideOut.myGiftsDetailsSave }}
      </AtomicUiButton>
      <div class="large-margin-top-m small-margin-bottom-s mobile-cta-s">
        <div class="grid-x grid-margin-x grid-margin-y align-center">
          <div class="cell small-6 grid-x">
            <AtomicUiButton variant="secondary" :expanded="true" :disabled="itemFullyPurchased" @click.stop="toggleDeleteModalOverlay(product)">
              {{ quickViewSlideOut.myGiftsDetailsRemoveItem }}
            </AtomicUiButton>
          </div>
          <div class="cell small-6 grid-x">
            <AtomicUiButton variant="secondary" :expanded="true" :disabled="disableAddToBagCta" :loading="isAddToBagLoading" @click="handleAddToBag">
              {{ disabledButtonText ? disabledButtonText : 'Add To Bag' }}
            </AtomicUiButton>
          </div>
        </div>
      </div>

      <div v-if="showRegistrantViewProductDetailsLink" class="text-center margin-top-xs">
        <a class="secondary-cta" target="blank" :href="productDetail?.identifier?.productUrl">View full product details</a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { updateGiftsAddedCount } from '~/utils/GiftCardUtil';
import { errorMessages, quickViewSlideOut } from 'config';
import { useQuickView } from '~/composables/useQuickView';
import useAddToBag from '~/composables/useAddToBag';
import { useQuickViewStore } from '~/stores/quickView';
import { useEnvPropsStore } from '~/stores/envProps';
import { useHeaderBannerStore } from '~/stores/headerBanner';

const props = defineProps({
  product: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  isRegistrant: {
    type: Boolean,
    required: false,
    default: false,
  },
  quantityRequested: {
    type: Number,
    required: false,
    default: 0,
  },
  quantityPurchased: {
    type: Number,
    required: false,
    default: 0,
  },
  quantityReceived: {
    type: Number,
    required: false,
    default: 0,
  },
  slideoutId: {
    type: String,
    required: true,
  },
});

const emits = defineEmits([
  'toggleDeleteModalOverlay',
  'closeSlideout',
  'openConfirmationModal',
  'scrollToSlideoutBottom',
  'registry-atb-error',
  'refresh-atb',
]);

const quickViewStore = useQuickViewStore();
const envPropsStore = useEnvPropsStore();
const headerBannerStore = useHeaderBannerStore();

const showUpdatePreferenceError = ref(false);
const showGiftGiverViewProductDetailsLink = ref(false);
const showRegistrantViewProductDetailsLink = ref(false);
const saveChangesButtonDisabled = ref(false);
const invalidVariantError = ref(false);

const newSelectedSize = computed(() => quickViewStore.selectedSize);
const newSelectedColor = computed(() => quickViewStore.selectedColor);

watch([newSelectedSize, newSelectedColor], () => {
  invalidVariantError.value = false;
  quickViewStore.setQuickViewSaveErrorState(false);
});

const {
  disabledButtonText,
  disableAddToBagCta,
  purchasedQuantity,
  requestedQuantity,
  productDetail,
  selectedColor,
  isAvailable,
  defaultUpcDetails,
  upcDetails,
  itemFullyPurchased,
  hasRequestDropdownChanged,
  hasPurchasedDropdownChanged,
  isColorOrSizeChanged,
  registryGUID,
  enableQuantityDropdown,
  upcsRefObj,
  isMew,
  saveChangedAPIResponse,
  handleAddToBag,
  errorState,
  toggleDeleteModalOverlay,
  isAddToBagLoading,
} = useQuickView({
  propQuantityRequested: props.quantityRequested,
  propQuantityPurchased: props.quantityPurchased,
  propIsRegistrant: props.isRegistrant,
  emits,
  propSlideoutId: props.slideoutId,
  propProduct: props.product,
});

useAddToBag();
const source = computed(() => envPropsStore.envProps.entryPoint.toUpperCase());
const orgId = computed(() => (envPropsStore.envProps.entryPoint.toUpperCase() === 'MCOM' ? 12 : 13));
const headerBanner = computed(() => headerBannerStore.headerBanner);

const setGiftsAddedCount = () => {
  headerBannerStore.setRegistryHomeTab(updateGiftsAddedCount(headerBanner.value));
};

const quickViewSaveErrorState = computed(() => quickViewStore.quickViewSaveErrorState);

const notification = computed(() => {
  if (invalidVariantError.value) {
    return {
      show: true,
      message: 'This color/size combination is not available',
    };
  }
  if (quickViewSaveErrorState.value) {
    return {
      show: true,
      message: saveChangedAPIResponse.value?.description || errorMessages.serverProcessingError,
    };
  }
  return {
    show: false,
    message: '',
  };
});

const isColorValidForSize = computed(() => {
  const originalColorId = Number(String(selectedColor.value || '').split('_')[0]);
  const originalSizeId = defaultUpcDetails.value?.size?.id;
  const sizeId = newSelectedSize.value || originalSizeId;
  const colorId = newSelectedColor.value || originalColorId;
  if (productDetail.value?.traits?.sizes?.sizeMap && sizeId && colorId) {
    const sizeDetails = productDetail.value.traits.sizes.sizeMap[sizeId];
    if (sizeDetails && sizeDetails.colors) {
      return sizeDetails.colors.includes(colorId);
    }
  }
  return true;
});

const disableSaveBtn = computed(() => {
  if (props.isRegistrant) {
    // enable Save CTA when the below conditions are satisfied
    // either requested or purchased are changed and upcdetails are not available
    // upc details are available and but not matched with default upc details or any of purchased/requested are changed
    return !(hasRequestDropdownChanged.value || hasPurchasedDropdownChanged.value || isColorOrSizeChanged.value);
  }
  return false;
});

const saveRegistrantQuantityChanges = async () => {
  invalidVariantError.value = false;
  if (!isColorValidForSize.value) {
    invalidVariantError.value = true;
    emits('scrollToSlideoutBottom');
    return;
  }
  saveChangesButtonDisabled.value = true;
  const oldUpcNumber = defaultUpcDetails.value?.identifier?.upcNumber
    ? defaultUpcDetails.value?.identifier?.upcNumber
    : props.product?.detail?.upcNumber;
  if (hasRequestDropdownChanged.value && hasPurchasedDropdownChanged.value) {
    await quickViewStore.updatePreference({
      orgId: orgId.value,
      registryGUID: registryGUID.value,
      oldUPC: oldUpcNumber,
      purchaseQuantity: purchasedQuantity.value - props.quantityPurchased, // old UPC purchase quantity
      requestedQuantity: requestedQuantity.value, // old UPC request quantity
      sourceSubSystem: isMew.value ? 'MEW' : 'Site',
      sourceSystem: source.value,
    });
  } else if (isColorOrSizeChanged.value && hasRequestDropdownChanged.value) {
    await quickViewStore.updatePreference({
      orgId: orgId.value,
      registryGUID: registryGUID.value,
      oldUPC: oldUpcNumber,
      newUPC: upcsRefObj.value[upcDetails.value?.upcId],
      purchaseQuantity: purchasedQuantity.value - props.quantityPurchased, // old UPC purchase quantity
      requestedQuantity: requestedQuantity.value, // old UPC request quantity
      sourceSubSystem: isMew.value ? 'MEW' : 'Site',
      sourceSystem: source.value,
    });
  } else if (isColorOrSizeChanged.value) {
    await quickViewStore.updatePreference({
      orgId: orgId.value,
      registryGUID: registryGUID.value,
      oldUPC: oldUpcNumber,
      newUPC: upcsRefObj.value[upcDetails?.value.upcId],
      purchaseQuantity: purchasedQuantity.value - props.quantityPurchased, // old UPC purchase quantity
      requestedQuantity: requestedQuantity.value, // old UPC request quantity
      sourceSubSystem: isMew.value ? 'MEW' : 'Site',
      sourceSystem: source.value,
    });
  } else if (hasRequestDropdownChanged.value) {
    await quickViewStore.updatePreference({
      orgId: orgId.value,
      registryGUID: registryGUID.value,
      oldUPC: oldUpcNumber,
      newUPC: null,
      purchaseQuantity: purchasedQuantity.value,
      requestedQuantity: requestedQuantity.value,
      sourceSubSystem: isMew.value ? 'MEW' : 'Site',
      sourceSystem: source.value,
    });
  } else if (hasPurchasedDropdownChanged.value) {
    await quickViewStore.addPurchase({
      orgId: orgId.value,
      registryGUID: registryGUID.value,
      purchaseItems: [
        {
          applicationId: 'Registrant',
          upc: oldUpcNumber,
          comment: 'adding purchased qty',
          completed: false,
          giftReceivedFlag: true,
          purchaseType: 'Manual',
          purchaserName: 'Updated by Registrant',
          quantity: purchasedQuantity.value - props.quantityPurchased,
          ringAssocDiv: 0,
          ringAssocId: 0,
          soldAssocDiv: 0,
          soldAssocId: 0,
          price: 0.0,
          sourceSubSystem: isMew.value ? 'MEW' : 'Site',
          sourceSystem: source.value,
          sttDate: new Date().toISOString().split('T')[0],
          thankyouSentFlag: false,
          transactionStatus: 'Matched',
          transactionType: 'Sale',
          sttStoreNumber: 129,
        },
      ],
    });
  }
  const isSuccess = quickViewStore.updatePreferenceStatus;
  saveChangesButtonDisabled.value = false;
  if (isSuccess) {
    if (orgId.value === 12) {
      fireTag('link', {
        event_name: 'edit registry product details',
        custom_link_name: 'edit product details save changes',
      });
    } else {
      fireTag('link', {
        event_name: 'edit product details save changes',
        custom_link_name: 'edit registry product details',
        link_name: 'edit registry product details',
      });
    }

    // Reload Header Banner data in the store
    if (hasRequestDropdownChanged.value || hasPurchasedDropdownChanged.value) {
      await headerBannerStore.updateHeaderBannerValues({
        orgId: orgId.value,
        registryGUID: registryGUID.value,
      });
      setGiftsAddedCount();
      const headerBannerAPIErrorResponse = headerBannerStore.headerBannerApiErrorResponse;
      const headerBannerAPIErrorDescription = headerBannerStore.errorDescription;

      if (headerBannerAPIErrorResponse) {
        fireTag('link', {
          event_name: 'Registry error',
          error_category: 'registry',
          error_code: [`REG${headerBannerAPIErrorResponse?.response?.data?.errors?.[0]?.code || '999'}`],
          error_label: [' '],
          error_message: [headerBannerAPIErrorDescription || errorMessages.serverProcessingError],
          error_misc: [' '],
          error_scope: ['Global'],
        });
      }
    }
    emits('closeSlideout');
    window.scrollTo(0, 0);
  } else {
    showUpdatePreferenceError.value = true;
    fireTag('link', {
      event_name: 'Registry error',
      error_category: 'registry',
      error_code: [`REG${saveChangedAPIResponse.value?.code || '999'}`],
      error_label: ['SAVE_CHANGES_BUTTON'],
      error_message: [saveChangedAPIResponse.value?.description || errorMessages.registrantItemSaveChangesError],
      error_misc: [' '],
      error_scope: ['Global'],
    });
  }
};
</script>

<style scoped lang="scss">
.mobile-cta-s {
  display: block;
  @include breakpoint(medium down) {
    display: none;
  }
}
.copyMsg {
  color: #e01a2b;
}
</style>
